import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';

interface MetricCardProps {
  label: string;
  value: number;
  prefix?: string;
  suffix?: string;
  change?: number;
  icon?: React.ReactNode;
  color?: 'emerald' | 'orange' | 'blue' | 'purple';
  delay?: number;
}

const colorMap = {
  emerald: {
    bg: 'bg-emerald-500/20',
    ring: 'ring-emerald-400/30',
    text: 'text-emerald-400',
    gradient: 'from-emerald-400 to-emerald-500',
  },
  orange: {
    bg: 'bg-orange-500/20',
    ring: 'ring-orange-400/30',
    text: 'text-orange-400',
    gradient: 'from-orange-400 to-orange-500',
  },
  blue: {
    bg: 'bg-blue-500/20',
    ring: 'ring-blue-400/30',
    text: 'text-blue-400',
    gradient: 'from-blue-400 to-blue-500',
  },
  purple: {
    bg: 'bg-purple-500/20',
    ring: 'ring-purple-400/30',
    text: 'text-purple-400',
    gradient: 'from-purple-400 to-purple-500',
  },
};

function AnimatedCounter({ target, duration = 2000 }: { target: number; duration?: number }) {
  const [current, setCurrent] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(2, -10 * progress);
      setCurrent(Math.round(target * eased));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [inView, target, duration]);

  return <span ref={ref}>{current.toLocaleString('es-ES')}</span>;
}

export function MetricCard({ label, value, prefix = '', suffix = '', change, icon, color = 'emerald', delay = 0 }: MetricCardProps) {
  const c = colorMap[color];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, delay }}
      className="rounded-2xl border border-white/10 bg-white/[0.06] p-5 backdrop-blur-md transition hover:border-emerald-400/20 hover:bg-white/[0.08]"
    >
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</p>
          <p className={`bg-gradient-to-r ${c.gradient} bg-clip-text text-3xl font-bold text-transparent`}>
            {prefix}<AnimatedCounter target={value} />{suffix}
          </p>
        </div>
        {icon && (
          <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${c.bg} ring-1 ${c.ring}`}>
            {icon}
          </div>
        )}
      </div>
      {change !== undefined && (
        <div className="mt-3 flex items-center gap-1.5">
          <span className={`text-xs font-semibold ${change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {change >= 0 ? '+' : ''}{change}%
          </span>
          <span className="text-xs text-slate-500">vs mes anterior</span>
        </div>
      )}
    </motion.div>
  );
}
